import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import { useBranding } from '../../../../context/BrandingContext';
import type { Rompimento } from '../../types';
import { API_BASE_URL } from '../../lib/api';
import { fmtDateTime } from '../../lib/occurrences';
import { areaLabel, copyToClipboard, getAreas, rompMensagem } from '../../lib/rompimentos';
import '../../rede.css';

const DEFAULT_CENTER: [number, number] = [-23.5015, -47.4526];
const AREA_STYLE: L.PathOptions = { color: '#dc2626', weight: 2, fillColor: '#dc2626', fillOpacity: 0.18 };

function fmtSince(iso: string | null): string {
  if (!iso) return '';
  let min = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  const dias = Math.floor(min / 1440);
  min -= dias * 1440;
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (dias > 0) return `há ${dias}d ${h}h`;
  if (h > 0) return `há ${h}h ${m}min`;
  return `há ${m}min`;
}

export default function PublicRompimentosPage() {
  const { clientName, logoSrc } = useBranding();
  const [list, setList] = useState<Rompimento[]>([]);
  const [loading, setLoading] = useState(true);
  const [updated, setUpdated] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);
  const layerById = useRef<Record<string, L.FeatureGroup>>({});
  const lastKey = useRef('');

  // Inicializa o mapa uma vez
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current).setView(DEFAULT_CENTER, 12);
    mapRef.current = map;
    L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
      attribution: '&copy; OpenStreetMap &copy; CARTO',
      maxZoom: 19
    }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    setTimeout(() => map.invalidateSize(), 150);
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Busca os rompimentos ativos + polling
  useEffect(() => {
    let alive = true;
    const fetchData = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/public/rompimentos`);
        const data = res.ok ? await res.json() : [];
        if (!alive) return;
        setList(Array.isArray(data) ? data : []);
        setUpdated(new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }));
      } catch {
        /* silencia erros de rede */
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetchData();
    const id = setInterval(fetchData, 30000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  // Desenha as áreas quando a lista muda
  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer) return;

    const key = list.map((r) => r._id).sort().join('|');
    const keyChanged = key !== lastKey.current;

    layer.clearLayers();
    layerById.current = {};
    const bounds = L.latLngBounds([]);

    list.forEach((r) => {
      const areas = getAreas(r);
      if (!areas.length) return;
      const layers = areas.map((a) =>
        a.tipo === 'circulo'
          ? L.circle([a.lat, a.lng], { radius: a.raio || 300, ...AREA_STYLE })
          : L.polygon((a.pontos || []).map((p) => [p.lat, p.lng] as [number, number]), AREA_STYLE)
      );
      const grp = L.featureGroup(layers).bindPopup(
        `<div style="font-weight:800;margin-bottom:4px">${r.titulo || 'Rompimento'}</div>` +
          `<div style="color:#475569;font-size:.8rem;line-height:1.5">🕒 Desde ${fmtDateTime(r.inicio)} <b>(${fmtSince(
            r.inicio
          )})</b><br>⭕ ${areaLabel(r)}${r.previsao ? `<br>🔧 Previsão: <b>${fmtDateTime(r.previsao)}</b>` : ''}</div>`
      );
      grp.addTo(layer);
      layerById.current[r._id] = grp;
      bounds.extend(grp.getBounds());
    });

    if (keyChanged && list.length && bounds.isValid()) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
    }
    lastKey.current = key;
  }, [list]);

  const focus = (r: Rompimento) => {
    const grp = layerById.current[r._id];
    const map = mapRef.current;
    if (!grp || !map) return;
    map.flyToBounds(grp.getBounds().pad(0.4), { maxZoom: 15, duration: 0.6 });
    grp.openPopup(grp.getBounds().getCenter());
  };

  const copyAviso = async (r: Rompimento, e: React.MouseEvent) => {
    e.stopPropagation();
    const ok = await copyToClipboard(rompMensagem(r));
    const btn = e.currentTarget as HTMLButtonElement;
    const orig = btn.textContent;
    btn.textContent = ok ? '✅ Copiado! Cole no WhatsApp' : '❌ Não foi possível copiar';
    setTimeout(() => {
      btn.textContent = orig;
    }, 2200);
  };

  return (
    <div className="rede-module relative h-screen w-screen overflow-hidden">
      <header className="absolute inset-x-0 top-0 z-[1100] flex h-14 items-center justify-between border-b border-slate-200 bg-white px-4 shadow-sm">
        <div className="flex items-center gap-2">
          {logoSrc && <img src={logoSrc} alt={clientName} className="h-7 w-auto object-contain" />}
          <span className="text-base font-extrabold text-sky-600">
            Rompimentos
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span
            className={
              list.length
                ? 'flex items-center gap-2 rounded-full border border-red-300 bg-red-50 px-3 py-1 text-xs font-bold text-red-600'
                : 'flex items-center gap-2 rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-600'
            }
          >
            <span className="h-2 w-2 rounded-full bg-current" />
            {list.length === 0 ? 'Tudo normal' : `${list.length} em andamento`}
          </span>
          {updated && <span className="hidden text-xs text-slate-400 sm:inline">Atualizado às {updated}</span>}
        </div>
      </header>

      <div ref={containerRef} className="absolute inset-x-0 bottom-0 top-14 z-0 bg-slate-200" />

      {/* Painel lateral */}
      <div className="absolute left-3 top-[68px] z-[1000] flex max-h-[calc(100vh-80px)] w-80 max-w-[calc(100vw-24px)] flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl">
        <div className="border-b border-slate-200 px-4 py-3 text-xs font-bold uppercase tracking-wide text-slate-500">
          Rompimentos em andamento
        </div>
        <div className="overflow-y-auto">
          {loading ? (
            <div className="px-4 py-6 text-center text-sm text-slate-400">Carregando...</div>
          ) : list.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-slate-400">
              <span className="mb-2 block text-2xl">✅</span>
              Nenhum rompimento em andamento no momento.
            </div>
          ) : (
            list.map((r) => (
              <div key={r._id} onClick={() => focus(r)} className="cursor-pointer border-b border-slate-100 px-4 py-3 hover:bg-slate-50">
                <div className="flex items-center gap-2 text-sm font-bold text-slate-800">
                  <span className="h-2.5 w-2.5 flex-shrink-0 rounded-full bg-red-500 shadow-[0_0_5px_rgba(220,38,38,.6)]" />
                  {r.titulo || 'Rompimento'}
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  🕒 Desde {fmtDateTime(r.inicio)} • {fmtSince(r.inicio)}
                  <br />⭕ {areaLabel(r)}
                  {r.previsao && (
                    <>
                      <br />🔧 Previsão: <b>{fmtDateTime(r.previsao)}</b>
                    </>
                  )}
                </div>
                {(r.cidades?.length > 0 || r.bairros?.length > 0) && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {r.cidades?.map((c) => (
                      <span key={'c' + c} className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500">
                        🏙 {c}
                      </span>
                    ))}
                    {r.bairros?.map((b) => (
                      <span key={'b' + b} className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500">
                        📍 {b}
                      </span>
                    ))}
                  </div>
                )}
                <button
                  onClick={(e) => copyAviso(r, e)}
                  className="mt-2 w-full rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5 text-xs font-bold text-sky-600 hover:border-sky-400 hover:bg-sky-50"
                >
                  📋 Copiar aviso para clientes
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {!loading && list.length === 0 && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 top-14 z-[900] flex items-center justify-center p-5">
          <div className="max-w-sm rounded-2xl border border-slate-200 bg-white px-8 py-7 text-center shadow-xl">
            <div className="text-5xl">✅</div>
            <h2 className="mt-2 text-lg font-bold text-slate-800">Nenhum rompimento em andamento</h2>
            <p className="mt-1 text-sm text-slate-500">
              No momento não há áreas com indisponibilidade registrada. Esta página atualiza sozinha.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
